import { z } from 'zod';

// The codes app-service answers when a `panel` route refuses a caller who lacks the permission the
// route declares: one shared code for every permission, plus the access-management resource's own,
// which predates it. The shared name lives in this one constant.
export const PERMISSION_REFUSAL_CODES = [
  'panel_permission_required',
  'access_manage_required',
] as const;

const [SHARED_CODE, ACCESS_MANAGE_CODE] = PERMISSION_REFUSAL_CODES;

// Mirrors the delivered contract, CBX-11713: the shared refusal is closed, and `detail` names the
// refused permission alone, null when the route declares none.
const SharedRefusalSchema = z
  .object({
    code: z.literal(SHARED_CODE),
    message: z.string(),
    detail: z.object({ permission: z.string().nullable() }).strict(),
  })
  .strict();

// The access-management resource's refusal as delivered: the code, sometimes a message, no
// `detail`. Its body is not recorded as closed, so keys it does not model are stripped.
const AccessManageRefusalSchema = z.object({
  code: z.literal(ACCESS_MANAGE_CODE),
  message: z.string().optional(),
});

export const PermissionRefusalSchema = z.discriminatedUnion('code', [
  SharedRefusalSchema,
  AccessManageRefusalSchema,
]);

export type PermissionRefusal = z.infer<typeof PermissionRefusalSchema>;

// What recognising a refusal reads: the code and nothing else. A `detail` or `message` that drifts
// from the mirror keeps a refusal a refusal; the contract tests catch the drift instead.
export const PermissionRefusalCodeSchema = z.object({ code: z.enum(PERMISSION_REFUSAL_CODES) });
